
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const Services = () => {
  const { t } = useTranslation();

  const services = [
    {
      icon: 'ri-hotel-line',
      title: t('hospitalityTitle'),
      description: t('hospitalityDesc'),
      features: [
        'Hotel Management Staff',
        'Restaurant Professionals',
        'Resort Operations Team',
        'Tourism Guides',
        'Event Management',
        'Customer Service Representatives'
      ]
    },
    {
      icon: 'ri-building-line',
      title: t('constructionTitle'),
      description: t('constructionDesc'),
      features: [
        'Civil Engineers',
        'Project Managers',
        'Skilled Construction Workers',
        'Safety Supervisors',
        'Equipment Operators',
        'Quality Control Inspectors'
      ]
    },
    {
      icon: 'ri-heart-pulse-line',
      title: t('medicalTitle'),
      description: t('medicalDesc'),
      features: [
        'Doctors & Specialists',
        'Registered Nurses',
        'Medical Technicians',
        'Pharmacists',
        'Healthcare Administrators',
        'Support Staff'
      ]
    },
    {
      icon: 'ri-computer-line',
      title: t('technicalTitle'),
      description: t('technicalDesc'),
      features: [
        'Software Developers',
        'Network Engineers',
        'Technical Support',
        'System Administrators',
        'Database Specialists',
        'IT Consultants'
      ]
    }
  ];

  const documentationSteps = t('documentationList', { returnObjects: true }) as string[];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
              {t('servicesTitle')}
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
              {t('servicesSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 sm:p-8 hover:shadow-lg transition-shadow duration-200">
                <div className="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-4 rtl:space-x-reverse mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mx-auto sm:mx-0">
                    <i className={`${service.icon} text-blue-600 text-2xl w-8 h-8 flex items-center justify-center`}></i>
                  </div>
                  <div className="text-center sm:text-left">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                      {service.description}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {service.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-2 rtl:space-x-reverse">
                      <i className="ri-check-line text-blue-600 w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documentation Process */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              {t('documentationTitle')}
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Complete end-to-end support for your GCC employment journey
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {documentationSteps.map((step, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-lg">
                <div className="flex items-center space-x-4 rtl:space-x-reverse mb-4">
                  <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                    Step {index + 1}
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                  {step}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Our Process</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              A streamlined approach to connecting talent with opportunities
            </p>
          </div>
          
          {/* Mobile Timeline */}
          <div className="block sm:hidden space-y-6">
            {[
              {
                title: 'Initial Consultation',
                description: 'Understanding your skills, experience, and career goals',
                icon: 'ri-user-search-line'
              },
              {
                title: 'Skills Assessment',
                description: 'Comprehensive evaluation of technical and soft skills',
                icon: 'ri-file-list-3-line'
              },
              {
                title: 'Job Matching',
                description: 'Connecting you with suitable opportunities in GCC countries',
                icon: 'ri-links-line'
              },
              {
                title: 'Documentation',
                description: 'Complete visa and employment documentation support',
                icon: 'ri-file-text-line'
              },
              {
                title: 'Deployment',
                description: 'Travel arrangements and arrival support',
                icon: 'ri-plane-line'
              },
              {
                title: 'Follow-up',
                description: 'Ongoing support for successful integration',
                icon: 'ri-customer-service-2-line'
              }
            ].map((step, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-lg">
                <div className="flex items-center space-x-3 rtl:space-x-reverse mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <i className={`${step.icon} text-blue-600 w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>

          {/* Desktop Timeline */}
          <div className="hidden sm:block relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-blue-200"></div>
            <div className="space-y-12">
              {[
                {
                  title: 'Initial Consultation',
                  description: 'Understanding your skills, experience, and career goals',
                  icon: 'ri-user-search-line'
                },
                {
                  title: 'Skills Assessment',
                  description: 'Comprehensive evaluation of technical and soft skills',
                  icon: 'ri-file-list-3-line'
                },
                {
                  title: 'Job Matching',
                  description: 'Connecting you with suitable opportunities in GCC countries',
                  icon: 'ri-links-line'
                },
                {
                  title: 'Documentation',
                  description: 'Complete visa and employment documentation support',
                  icon: 'ri-file-text-line'
                },
                {
                  title: 'Deployment',
                  description: 'Travel arrangements and arrival support',
                  icon: 'ri-plane-line'
                },
                {
                  title: 'Follow-up',
                  description: 'Ongoing support for successful integration',
                  icon: 'ri-customer-service-2-line'
                }
              ].map((step, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                  <div className={`w-full max-w-md ${index % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-lg">
                      <div className={`flex items-center space-x-3 rtl:space-x-reverse mb-3 ${index % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <i className={`${step.icon} text-blue-600 w-5 h-5 flex items-center justify-center`}></i>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-gray-600">{step.description}</p>
                    </div>
                  </div>
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full border-4 border-white"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            Ready to Start Your Career in the GCC?
          </h2>
          <p className="text-lg sm:text-xl text-blue-100 mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
            Let our experienced team guide you through every step of the recruitment process
          </p>
          <button className="bg-white text-blue-600 hover:bg-gray-100 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg transition-colors duration-200 whitespace-nowrap">
            {t('contact')}
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
